import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AskAiCellValue } from '@/types/askAi';

const PIE_SLICE_CAP = 8;
const HORIZONTAL_BAR_THRESHOLD = 12;

const PALETTE = [
  'hsl(var(--primary))',
  'hsl(var(--accent))',
  'hsl(var(--destructive))',
  'hsl(var(--muted-foreground))',
  'hsl(var(--secondary))',
  'hsl(var(--ring))',
];

// Narrowed, non-null shape — ChartDispatcher only reaches this component
// once it has confirmed `type`, `x`, and `y` are all present.
export interface AskAiChartHintLegacy {
  type: 'bar' | 'line' | 'pie';
  x: string;
  y: string;
}

interface Props {
  columns: string[];
  rows: AskAiCellValue[][];
  hint: AskAiChartHintLegacy;
}

interface ChartDatum {
  name: string;
  value: number;
}

function buildData(
  columns: string[],
  rows: AskAiCellValue[][],
  hint: AskAiChartHintLegacy,
): { data: ChartDatum[]; xIsString: boolean } | null {
  const xi = columns.indexOf(hint.x);
  const yi = columns.indexOf(hint.y);
  if (xi < 0 || yi < 0) return null;

  let xIsString = false;
  const data: ChartDatum[] = [];
  for (const row of rows) {
    const xv = row[xi];
    const yv = row[yi];
    if (yv === null || yv === undefined) continue;
    const yNum = typeof yv === 'number' ? yv : Number(yv);
    if (Number.isNaN(yNum)) continue;
    const name = xv === null || xv === undefined ? '∅' : String(xv);
    if (typeof xv === 'string') xIsString = true;
    data.push({ name, value: yNum });
  }
  return { data, xIsString };
}

function collapsePieSlices(data: ChartDatum[]): ChartDatum[] {
  if (data.length <= PIE_SLICE_CAP) return data;
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const top = sorted.slice(0, PIE_SLICE_CAP - 1);
  const rest = sorted.slice(PIE_SLICE_CAP - 1);
  const otherSum = rest.reduce((acc, d) => acc + d.value, 0);
  return [...top, { name: 'Other', value: otherSum }];
}

const tooltipStyle = {
  background: 'hsl(var(--popover))',
  border: '1px solid hsl(var(--border))',
  fontSize: 12,
  color: 'hsl(var(--popover-foreground))',
};

export function AskAiChartLegacy({ columns, rows, hint }: Props) {
  const built = useMemo(() => buildData(columns, rows, hint), [columns, rows, hint]);

  if (!built || built.data.length === 0) {
    return null;
  }

  const { data, xIsString } = built;

  try {
    if (hint.type === 'pie') {
      const pieData = collapsePieSlices(data);
      return (
        <div className="h-60 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                outerRadius={80}
                label={(d) => d.name}
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      );
    }

    if (hint.type === 'line') {
      return (
        <div className="h-60 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    }

    const horizontal = xIsString && data.length > HORIZONTAL_BAR_THRESHOLD;

    return (
      <div className="h-60 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout={horizontal ? 'vertical' : 'horizontal'}
            margin={{ top: 8, right: 12, bottom: 4, left: horizontal ? 60 : 0 }}
          >
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
            {horizontal ? (
              <>
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
              </>
            ) : (
              <>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                <YAxis tick={{ fontSize: 10 }} />
              </>
            )}
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="value" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  } catch {
    return null;
  }
}
